import {createStyles} from '@mantine/core';
import {useActorRef, useSelector} from '@xstate/react';
import React, {useCallback, useState} from 'react';

import {assert} from 'Asserts';
import {CreatePlateDesignTemplateFromScratchParams} from 'plates/dao/PlateDesignTemplateDAO';
import {createScratchPlates, updateScratchPlates} from 'plates/dao/ScratchPlateDAO';
import {ConcentrationLayerContext} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {SchematizedDisplayLayerMetadata} from 'samples/plate-maps/PlateMapConstants';
import {PlateSchema, PlateSize} from 'samples/storables/StorableSchemaConstants';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavSize, StepNavVariant} from 'ui-library/StepNav';

import useWellSchemaLayers from '../hooks/useWellSchemaLayers';
import ConnectedDefineScratchPlateMapStep from './ConnectedDefineScratchPlateMapStep';
import {
  buildConcentrationLayers,
  buildMetadataLayers,
  buildParameterLayers,
  buildWells,
  getInvalidConcentrationLayerTooltip,
  getInvalidMetadataLayerTooltip,
} from './CreateScratchPlateRecordsModalUtil';
import {CreationMode} from './DefineScratchPlateSettingsStep';
import DefineScratchPlateSettingsStep from './DefineScratchPlateSettingsStep';
import {
  getScratchPlateDesignModalSteps,
  ScratchPlateDesignState,
  ScratchPlateDesignModalStep,
  isLastScratchPlateDesignModalStep,
} from './ScratchPlateDesignModalUtil';
import {ScratchPlateModalContext, ScratchPlateMachine} from './ScratchPlateModalMachine';
import usePlateDesignTemplateLoading from './usePlateDesignTemplateLoading';

const MAX_PLATE_VIEW_HEIGHT = 'calc(100vh - 400px)';

async function saveScratchPlateFromModal(
  authParentApiId: string,
  state: ScratchPlateModalContext,
  concentrationLayerContext: ConcentrationLayerContext,
  existingId?: string | null
): Promise<string> {
  const {name, plateSize, plateMap, plateSchema} = state;
  if (name == null || plateMap == null || plateSize == null) {
    throw new Error('saveScratchPlateFromModal called with invalid (null) inputs');
  }

  const [height, width] = plateSize;
  const wells = buildWells(plateMap);
  const concentrationLayers = buildConcentrationLayers(plateMap, concentrationLayerContext);
  const metadataLayers = buildMetadataLayers(plateMap);
  const parameterLayers = buildParameterLayers(plateMap);

  if (existingId != null) {
    await updateScratchPlates([
      {
        id: existingId,
        name,
        height,
        width,
        wells,
        concentrationLayers,
        metadataLayers,
        parameterLayers,
        // if the plate schema is null, then the update will explicitly set the schema link to None
        plateSchemaId: plateSchema?.id ?? null,
      },
    ]);
    return existingId;
  }

  const response = await createScratchPlates([
    {
      authParentId: authParentApiId,
      name,
      height,
      width,
      wells,
      concentrationLayers,
      metadataLayers,
      parameterLayers,
      // if the plate schema is null, then the create will not link a schema
      plateSchemaId: plateSchema?.id,
    },
  ]);
  const createdId = response.createdIds[0];
  assert(createdId != null, 'createScratchPlates returned no IDs', {response});
  return createdId;
}

export type ScratchPlateDesignFromScratchParams = Omit<
  CreatePlateDesignTemplateFromScratchParams,
  'templateCollectionId'
>;

export interface ScratchPlateDesignModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (scratchPlateId: string) => void;
  authParentApiId: string;
  enableCreateFromTemplate?: boolean;
  initialPlateState?: ScratchPlateDesignState;
  existingPlateId?: string;
  onRefetch?: () => void;
  /**
   * If defined, the modal is used to create a plate design template directly, rather than just creating a
   * scratch plate design.
   */
  onCreatePlateDesignTemplate?: (params: ScratchPlateDesignFromScratchParams) => Promise<void>;
}

export default function ScratchPlateDesignModal({
  onRequestClose,
  onRequestSubmit,
  authParentApiId,
  enableCreateFromTemplate = true,
  initialPlateState,
  existingPlateId,
  onRefetch,
  onCreatePlateDesignTemplate,
}: ScratchPlateDesignModalProps): JSX.Element {
  const {classes} = useStyles();
  const isEditing = existingPlateId != null;

  // All state managed by ScratchPlateMachine
  const plateActor = useActorRef(ScratchPlateMachine, {input: initialPlateState});
  const plateState = useSelector(plateActor, (snap) => snap.context);
  const concentrationLayerContext = useSelector(
    plateState.concentrationLayerMachine!,
    (snap) => snap.context
  );

  // Schema field layers — updates when the plate schema changes
  const onLoadWellSchemaFields = useCallback((_schemaFieldLayers: Array<SchematizedDisplayLayerMetadata>) => {
    // No initial schema field selection on load
  }, []);
  const wellSchemaLayers = useWellSchemaLayers(
    plateState.plateSchema?.containerSchemaId ?? null,
    onLoadWellSchemaFields,
    []
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    creationMode,
    onChangeCreationMode,
    selectedTemplateId,
    onSelectTemplate,
    isTemplateLoading,
    templateLoaderElement,
  } = usePlateDesignTemplateLoading(plateActor);

  // Either progress to the next step in the modal, or save the scratch plate and submit the modal
  async function handleSubmit(): Promise<void> {
    if (isLastScratchPlateDesignModalStep(plateState.currentStep)) {
      setIsSubmitting(true);
      try {
        let scratchPlateId = '';
        if (
          onCreatePlateDesignTemplate != null &&
          plateState.plateMap != null &&
          plateState.plateSize != null
        ) {
          const [height, width] = plateState.plateSize;
          if (plateState.name == null) {
            throw new Error('onCreatePlateDesignTemplate called with invalid (null) inputs');
          }
          await onCreatePlateDesignTemplate({
            name: plateState.name,
            height,
            width,
            wells: buildWells(plateState.plateMap),
            concentrationLayers: buildConcentrationLayers(plateState.plateMap, concentrationLayerContext),
            metadataLayers: buildMetadataLayers(plateState.plateMap),
            parameterLayers: buildParameterLayers(plateState.plateMap),
            plateSchemaId: plateState.plateSchema?.id,
          });
        } else {
          scratchPlateId = await saveScratchPlateFromModal(
            authParentApiId,
            plateState,
            concentrationLayerContext,
            existingPlateId
          );
        }
        onRequestSubmit(scratchPlateId);
        onRefetch?.();
      } finally {
        setIsSubmitting(false);
      }
    } else {
      plateActor.send({type: 'nextStep'});
    }
  }

  // Go back to the previous step in the modal
  function handleBack(): void {
    plateActor.send({type: 'previousStep'});
  }

  const steps = getScratchPlateDesignModalSteps();
  const statusByStepKey: {[key in ScratchPlateDesignModalStep]: StepItemStatus | null} = {
    [ScratchPlateDesignModalStep.DEFINE_SETTINGS]: null,
    [ScratchPlateDesignModalStep.DEFINE_PLATE_MAP]: null,
  };

  // componentBody and modalButtons are different depending which step we're in
  let componentBody: JSX.Element = <></>;
  let modalButtons: Array<ModalButton> = [];
  if (plateState.currentStep === ScratchPlateDesignModalStep.DEFINE_SETTINGS) {
    const onChangeName = (newName: string): void => {
      plateActor.send({type: 'updateName', name: newName});
    };
    const onChangePlateSize = (newPlateSize: PlateSize | null): void => {
      plateActor.send({type: 'updatePlateSize', plateSize: newPlateSize});
    };
    const onChangePlateSchema = (newPlateSchema: PlateSchema | null): void => {
      plateActor.send({type: 'updatePlateSchema', plateSchema: newPlateSchema});
    };
    componentBody = (
      <DefineScratchPlateSettingsStep
        name={plateState.name}
        onChangeName={onChangeName}
        plateSize={plateState.plateSize}
        onChangePlateSize={onChangePlateSize}
        plateSchema={plateState.plateSchema}
        onChangePlateSchema={onChangePlateSchema}
        creationMode={isEditing ? CreationMode.SCRATCH : creationMode}
        onChangeCreationMode={!enableCreateFromTemplate || isEditing ? undefined : onChangeCreationMode}
        selectedTemplateId={selectedTemplateId}
        onSelectTemplate={onSelectTemplate}
        isTemplateLoading={isTemplateLoading}
      />
    );
    modalButtons = [
      {text: 'Cancel', cancel: true},
      {
        text: 'Next',
        submit: true,
        disabled:
          plateState.name == null ||
          plateState.name.trim() === '' ||
          plateState.plateSize == null ||
          isTemplateLoading,
      },
    ];
  } else if (plateState.currentStep === ScratchPlateDesignModalStep.DEFINE_PLATE_MAP) {
    componentBody = (
      <ConnectedDefineScratchPlateMapStep
        plateActor={plateActor}
        wellSchemaLayers={wellSchemaLayers}
        maxPlateViewHeight={MAX_PLATE_VIEW_HEIGHT}
      />
    );
    const invalidConcentrationLayerTooltip = getInvalidConcentrationLayerTooltip(
      concentrationLayerContext,
      plateState.plateMap
    );
    const invalidMetadataLayerTooltip = getInvalidMetadataLayerTooltip(plateState.plateMap);
    const invalidLayerTooltip = invalidConcentrationLayerTooltip ?? invalidMetadataLayerTooltip;
    const submitButtonText = isSubmitting
      ? isEditing
        ? 'Saving...'
        : 'Creating...'
      : isEditing
        ? 'Save design'
        : 'Create design';
    modalButtons = [
      {text: 'Back', onClick: handleBack},
      {
        text: submitButtonText,
        submit: true,
        disabled: isSubmitting || plateState.plateMap == null || invalidLayerTooltip != null,
        tooltipTitle: invalidLayerTooltip,
      },
    ];
  } else {
    throw new Error(`Unexpected step: ${plateState.currentStep}`);
  }

  return (
    <Modal
      title={isEditing ? 'Edit plate design' : 'Create plate design'}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      fullWidth={plateState.currentStep !== ScratchPlateDesignModalStep.DEFINE_SETTINGS}
      mediumLarge={plateState.currentStep === ScratchPlateDesignModalStep.DEFINE_SETTINGS}
      expandHeight={true}
      buttons={modalButtons}
      wrapInForm={false}
      closeableOnEscapeKey={false}
    >
      <StepNav<ScratchPlateDesignModalStep>
        currentStepKey={plateState.currentStep}
        onUpdateCurrentStep={undefined}
        steps={steps}
        statusByStep={statusByStepKey}
        size={StepNavSize.SMALL}
        variant={StepNavVariant.FLAT}
      />
      <hr className={classes.horizontalLine} />
      {componentBody}
      {plateState.currentStep === ScratchPlateDesignModalStep.DEFINE_SETTINGS &&
        creationMode === CreationMode.TEMPLATE &&
        templateLoaderElement}
    </Modal>
  );
}

const useStyles = createStyles(() => ({
  horizontalLine: {
    marginLeft: '-16px',
    marginRight: '-16px',
  },
}));
